import * as React from "react";
import { Button, makeStyles, Paper, Typography } from "@material-ui/core";
import { Colors } from "styles/Colors";
import DarkDataGrid from "common/components/DarkDataGrid/DarkDataGrid";
import { utc } from "./utcDate";

const useStyles = makeStyles((theme) => ({
	card: {
		backgroundColor: Colors.panel,
		padding: theme.spacing(3),
		marginBottom: theme.spacing(2),
	},
	head: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
	headActions: { display: "flex", gap: theme.spacing(1) },
	grid: { marginBottom: theme.spacing(2) },
	facts: {
		display: "grid",
		gridTemplateColumns: "auto 1fr",
		columnGap: theme.spacing(2),
		rowGap: 2,
	},
	label: { opacity: 0.7 },
	section: { marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
}));

const columns = [
	{ field: "stage", headerName: "Stage", flex: 2.2, minWidth: 125 },
	{ field: "stageType", headerName: "Type", flex: 1.1, minWidth: 70 },
	{ field: "startTime", headerName: "Start", flex: 1.7, minWidth: 100 },
	{ field: "decided", headerName: "Decided", flex: 1.9, minWidth: 115 },
	{ field: "slots", headerName: "Slots", flex: 1, minWidth: 60 },
	{ field: "teams", headerName: "Teams", flex: 1, minWidth: 60 },
	{ field: "gate", headerName: "Needs", flex: 1, minWidth: 60 },
];

const decidedProgress = (stage) => {
	if (stage.bracketPicks) {
		return `${stage.decidedPicks}/${stage.bracketPicks} matches`;
	}
	return stage.groupPicks
		? `${stage.advancingPicks}/${stage.groupPicks} advancing`
		: "-";
};

function Facts({ rows, classes }) {
	return (
		<div className={classes.facts}>
			{rows.map(([label, value]) => (
				<React.Fragment key={label}>
					<Typography variant="body2" className={classes.label}>
						{label}
					</Typography>
					<Typography variant="body2">{value}</Typography>
				</React.Fragment>
			))}
		</div>
	);
}

export default function EventStatusCard({
	diagnostics,
	onForceSync,
	onNotifyStageOpen,
}) {
	const classes = useStyles();
	if (!diagnostics) {
		return null;
	}

	const advance = diagnostics.advance ?? {};
	const current = (diagnostics.stages ?? []).find(
		(s) => s.stageIndex === diagnostics.currentStage
	);

	const stages = diagnostics.stages ?? [];
	const rows = stages.map((s) => ({
		id: s.stageIndex,
		stage: `${s.stageIndex}. ${s.name ?? "(unnamed)"}`,
		stageType: s.stageType ?? "-",
		startTime: utc(s.startTime),
		decided: decidedProgress(s),
		slots: s.bracketPicks ? s.filledSlots : "-",
		teams: s.teams,
		gate: s.teamsToOpenPicks ?? "-",
	}));

	const picksProtected = stages
		.filter((s) => s.lockScoring)
		.map((s) => s.stageIndex);

	const picks = diagnostics.picksOpen
		? `open, locks ${utc(diagnostics.lockDate)} UTC`
		: `locked${diagnostics.lockDate ? ` since ${utc(diagnostics.lockDate)} UTC` : ""}`;

	const automation = diagnostics.automation ?? {};

	return (
		<Paper className={classes.card} elevation={3}>
			<div className={classes.head}>
				<Typography variant="h6">
					{diagnostics.name} (#{diagnostics.id})
				</Typography>
				<div className={classes.headActions}>
					<Button
						variant="outlined"
						color="primary"
						onClick={onNotifyStageOpen}
					>
						Send Stage Push
					</Button>
					<Button variant="outlined" color="primary" onClick={onForceSync}>
						Force Sync
					</Button>
				</div>
			</div>

			<Facts
				classes={classes}
				rows={[
					[
						"Current stage",
						current
							? `${current.stageIndex} - ${current.name ?? "(unnamed)"}`
							: `${diagnostics.currentStage ?? "-"}`,
					],
					["Picks", picks],
					[
						"Event window",
						`${utc(diagnostics.eventStartDate)} to ${utc(
							diagnostics.eventEndDate
						)} UTC`,
					],
					...(picksProtected.length
						? [
								[
									"Picks kept from re-sync",
									`stage ${picksProtected.join(", ")} (LockScoring)`,
								],
						  ]
						: []),
				]}
			/>

			<Typography variant="subtitle2" className={classes.section}>
				Advance to next stage
			</Typography>
			<Facts
				classes={classes}
				rows={[
					["Next stage", advance.nextStageIndex ?? "-"],
					["Stopped at", advance.blocker ?? "-"],
					["Reading", advance.detail ?? "-"],
					[
						"Teams counted",
						advance.teamsRequired == null
							? "not reached"
							: `${advance.teamsCounted} of ${advance.teamsRequired}`,
					],
					[
						"Next stage start",
						advance.nextStageStartTime
							? `${utc(advance.nextStageStartTime)} UTC`
							: "not set",
					],
				]}
			/>

			<Typography variant="subtitle2" className={classes.section}>
				Stages (times UTC)
			</Typography>
			<DarkDataGrid
				className={classes.grid}
				rows={rows}
				columns={columns}
				autoHeight
				hideFooter
				disableSelectionOnClick
				disableColumnMenu
			/>

			<Typography variant="subtitle2" className={classes.section}>
				Sync
			</Typography>
			<Facts
				classes={classes}
				rows={[
					["Last fetch", `${utc(diagnostics.lastFetchedAt)} UTC`],
					[
						"Flagged for resync",
						diagnostics.liquipediaNotifiedChange ? "yes" : "no",
					],
					[
						"Last stored payload",
						diagnostics.payloadFetchedAt
							? `${utc(diagnostics.payloadFetchedAt)} UTC - ${
									diagnostics.payloadWiki
							  } / ${diagnostics.payloadPage}`
							: "none stored",
					],
					[
						"Stage-opened push notif",
						diagnostics.lastOpenedStageNotified == null
							? "never sent"
							: `sent through stage ${diagnostics.lastOpenedStageNotified}`,
					],
					[
						"Automation errors (3h)",
						automation.errorsLast3h > 0
							? `${automation.errorsLast3h} - last ${utc(
									automation.lastErrorAt
							  )} UTC: ${automation.lastErrorMessage}`
							: "0",
					],
					[
						"Last rate limit",
						automation.lastRateLimitAt
							? `${utc(automation.lastRateLimitAt)} UTC`
							: "none recorded",
					],
				]}
			/>
		</Paper>
	);
}
